<template>
   <div
      class="flex flex-col gap-1"
      :class="{
         'w-full': grow,
      }"
   >
      <Label v-if="label" :disabled="disabled">{{ label }}</Label>
      <div class="relative">
         <EditorContent :editor="editor" v-model="model" />
         <p v-if="maxLength" class="text-grey absolute bottom-2 right-4">
            {{ model?.length || 0 }}/{{ maxLength }}
         </p>
      </div>

      <div class="flex items-center gap-1">
         <div
            v-for="group in commands"
            class="bg-light-grey flex items-center gap-1 rounded-[6px] p-1"
         >
            <div
               v-for="command in group"
               class="h-fit rounded-[6px] px-1.5 py-0.5"
               :class="{
                  '!bg-primary': command.isActive(),
                  __clickable: !disabled,
                  'opacity-80': disabled,
               }"
               @click="() => (disabled ? null : command.toggle())"
            >
               <Icon :name="command.icon" :class="{ 'text-secondary': command.isActive() }" />
            </div>
         </div>
      </div>
   </div>

   <Modal
      v-model="linkModalOpen"
      size="md"
      :title="t('web.components.rich-text-editor.link-modal-title')"
      :confirm-button="{
         action: () => formRef?.submit(),
         text: t('common.add'),
         disabled: actionDisabled,
      }"
   >
      <Form ref="formRef" v-model="formData" @submit="handleSubmit" @dirty="(e) => (dirty = e)">
         <Select grow v-bind="formData.type" v-model="formData.type.value" />
         <TextInput grow v-bind="formData.text" v-model="formData.text.value" />
         <TextInput grow v-bind="formData.value" v-model="formData.value.value" />
      </Form>
   </Modal>
</template>

<script lang="ts" setup>
import { useFormData } from '../../composables/form-data'
import { isEmail, isPhone, isUrl } from '../../utils/form-validator'
import type Form from './Form.vue'

import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

type Props = {
   label?: string
   placeholder?: string
   error?: string
   disabled?: boolean
   grow?: boolean
   maxLength?: number
}

const props = withDefaults(defineProps<Props>(), {})
const model = defineModel<string>()

const { t } = useI18n()

const dirty = ref<boolean>(false)
const formRef = ref<InstanceType<typeof Form>>()
const linkModalOpen = ref<boolean>(false)
const editor = ref<Editor | null>(null)

const actionDisabled = computed(() => props.disabled || !dirty.value)

const initEditor = () => {
   return new Editor({
      extensions: [StarterKit],
      content: model.value || props.placeholder,
      editable: !props.disabled,
      editorProps: {
         attributes: {
            class: `bg-light-grey rounded-small overflow-y-auto p-m placeholder-grey min-h-52 resize-none px-4 py-2 focus:outline-none ${
               props.grow ? 'w-full' : ''
            } ${props.disabled ? 'opacity-80' : ''} ${props.error ? 'border border-red' : ''}`,
         },
      },
      onUpdate: () => {
         const content = editor.value?.getHTML()
         model.value = content === '<p></p>' ? '' : content
      },
   })
}

editor.value = initEditor()

const updateEditorAttributes = () => {
   if (!editor.value) return

   const newClass = `bg-light-grey rounded-small overflow-y-auto p-m placeholder-grey min-h-52 resize-none px-4 py-2 focus:outline-none ${
      props.grow ? 'w-full' : ''
   } ${props.disabled ? 'opacity-80' : ''} ${props.error ? 'border border-red' : ''}`

   editor.value.setOptions({
      editorProps: {
         attributes: {
            class: newClass,
         },
      },
   })

   editor.value.setEditable(!props.disabled)
}

const commands = computed(() => [
   [
      {
         isActive: () => editor.value?.isActive('heading', { level: 2 }) ?? false,
         toggle: () => editor.value?.chain().focus().toggleHeading({ level: 2 }).run(),
         icon: `h1`,
      },
      {
         isActive: () => editor.value?.isActive('heading', { level: 3 }) ?? false,
         toggle: () => editor.value?.chain().focus().toggleHeading({ level: 3 }).run(),
         icon: `h2`,
      },
      {
         isActive: () => editor.value?.isActive('heading', { level: 4 }) ?? false,
         toggle: () => editor.value?.chain().focus().toggleHeading({ level: 4 }).run(),
         icon: `h3`,
      },
   ],
   [
      {
         toggle: () => editor.value?.chain().focus().toggleStrike().run(),
         isActive: () => editor.value?.isActive('strike') ?? false,
         icon: 'strikethrough',
      },
      {
         toggle: () => editor.value?.chain().focus().toggleBold().run(),
         isActive: () => editor.value?.isActive('bold') ?? false,
         icon: 'bold',
      },
      {
         toggle: () => editor.value?.chain().focus().toggleItalic().run(),
         isActive: () => editor.value?.isActive('italic') ?? false,
         icon: 'italic',
      },
      {
         toggle: () => editor.value?.chain().focus().toggleUnderline().run(),
         isActive: () => editor.value?.isActive('underline') ?? false,
         icon: 'underline',
      },
   ],
   [
      {
         toggle: () => editor.value?.chain().focus().toggleBulletList().run(),
         isActive: () => editor.value?.isActive('bulletList') ?? false,
         icon: 'list-bullet',
      },
      {
         toggle: () => editor.value?.chain().focus().toggleOrderedList().run(),
         isActive: () => editor.value?.isActive('orderedList') ?? false,
         icon: 'numbered-list',
      },
      {
         toggle: () => {
            linkModalOpen.value = true
         },
         isActive: () => editor.value?.isActive('link') ?? false,
         icon: 'link',
      },
   ],
])

const formData = useFormData(
   () => ({
      type: {
         value: 'url',
         label: t('form.link-type'),
         type: 'select' as const,
         options: [
            { id: 'url', value: t('common.url') },
            { id: 'email', value: t('common.email') },
            { id: 'tel', value: t('common.phone') },
         ],
         required: true,
      },
      text: {
         value: '',
         label: t('form.text'),
         placeholder: t('form.text-placeholder'),
         type: 'text' as const,
         required: true,
      },
      value: {
         value: '',
         label: t('form.value'),
         placeholder: t('form.value-placeholder'),
         type: 'text' as const,
         required: true,
         validator: (v, form) =>
            form.type.value === 'url'
               ? isUrl(v)
               : form.type.value === 'email'
                 ? isEmail(v)
                 : isPhone(v),
      },
   }),
   { resetOn: linkModalOpen }
)

watch(
   linkModalOpen,
   (isOpen, wasOpen) => {
      if (isOpen && !wasOpen) dirty.value = false
   },
   { flush: 'post' }
)

const handleSubmit = async (data: typeof formData) => {
   linkModalOpen.value = false

   if (data.type.value === 'url') {
      editor.value
         ?.chain()
         .focus()
         .extendMarkRange('link')
         .setLink({
            href: `http://${data.value.value.replace('https://', '').replace('http://', '')}`,
         })
         .insertContent(data.text.value)
         .run()
   } else if (data.type.value === 'email') {
      editor.value
         ?.chain()
         .focus()
         .extendMarkRange('link')
         .setLink({ href: `mailto:${data.value.value}` })
         .insertContent(data.text.value)
         .run()
   } else if (data.type.value === 'tel') {
      editor.value
         ?.chain()
         .focus()
         .extendMarkRange('link')
         .setLink({ href: `tel:${data.value.value}` })
         .insertContent(data.text.value)
         .run()
   }
}

watch(model, (value) => {
   const isSame = editor.value?.getHTML() === value

   if (isSame) {
      return
   }

   editor.value?.commands.setContent(value || '')
})

watch([() => props.disabled, () => props.grow, () => props.error], () => {
   updateEditorAttributes()
})

onBeforeUnmount(() => {
   editor.value?.destroy()
})
</script>

<style lang="postcss">
.ProseMirror {
   a {
      cursor: pointer;
      color: #614ee7;
      text-decoration: underline;
   }

   ul,
   ol {
      padding-left: 1.5rem;
      margin: 0.5rem 0;
   }

   ul li {
      list-style-type: disc;
      margin: 0.25rem 0;
   }

   ol li {
      list-style-type: decimal;
      margin: 0.25rem 0;
   }

   li {
      padding-left: 0.25rem;
   }
}
</style>
